
import React, { useState } from "react"; 
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Grid, FileButton, Button as MButton, Autocomplete, Textarea, Select as MSelect,
  NumberInput, Group,  Badge,
  Stack, 
  Container, 
  Box  } from '@mantine/core';

import { FormattedMessage, useIntl } from "react-intl";
import { IconGripVertical } from "@tabler/icons-react";
import { getLocationsList, getProductStateList, getProductsList, getUOMList, setUOMList, getProductNameDescription, setProductNameDescription  } from "../Redux/products/slice";
import { getCategories } from "../Redux/options/slice";

function DescriptionList({ title, description, setDescription }) {
  // Get productNameDescription from Redux store
  const productNameDescription = useSelector((state) => state.products.productNameDescription);

  // Filter the productNameDescription based on title
  const productNameDescriptionList = productNameDescription?.filter(
    (item) => item.ProductNameId === title
  ) || [];

  // Local state to track temporary changes
  const [localDescriptions, setLocalDescriptions] = useState(() =>
    productNameDescriptionList.map((desc) => ({
      ...desc,
      descriptiontext: desc.descriptiontext || "", // Ensure `descriptiontext` exists
    }))
  );

  const handleDescriptionChange = (id, value) => {
    // Update local state
    const updatedLocalDescriptions = localDescriptions.map((desc) =>
      desc.DescriptionId === id ? { ...desc, description: value } : desc
    );
    setLocalDescriptions(updatedLocalDescriptions);

    // Construct the combined description string
    const descriptionString = updatedLocalDescriptions
      .map((item) => `${item.Description || "N/A"}: ${item.description || "N/A"} `)
      .join("\n\r");

    // Update the parent description state
    setDescription(descriptionString);
  };

  useEffect(() => {
    setLocalDescriptions(
      productNameDescriptionList.map((desc) => ({
        ...desc,
        descriptiontext: desc.descriptiontext || "", // Ensure `descriptiontext` exists
      }))
    );
  }, [productNameDescriptionList]);

  return productNameDescriptionList && productNameDescriptionList.length > 0 ? (
    <Box>
      {productNameDescriptionList.map((desc) => (
        <Group
          key={desc.DescriptionId}
          mt="xs"
          spacing="xs"
          noWrap
          align="start"
          style={{
            width: "100%",
          }}
        >
          <IconGripVertical size={18} style={{ flexShrink: 0 }} />
          <Badge
            color="blue"
            size="lg"
            styles={{
              root: {
                height: 'auto',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                padding: '8px',
                lineHeight: 1.4,
                textAlign: 'left',
                flex: '0 0 auto',
                minWidth: '200px', 
                maxWidth: '300px'
              },
              label: {
                whiteSpace: 'pre-wrap',
                display: 'block',
                width: '100%', 
              },
              inner: {
                width: '100%',
                overflow: 'visible'
              }
            }}
            title={desc.Description || "N/A"}
          >
            {desc.Description || "N/A"}
          </Badge>
          <Textarea
            placeholder="Value"
            value={desc.descriptiontext}
            onChange={(e) => handleDescriptionChange(desc.DescriptionId, e.target.value)}
            autosize
            minRows={2}
            maxRows={4}
            style={{ 
              flexGrow: 1,
              width: 'auto' 
            }}
          />
        </Group>
      ))}
    </Box>
  ) : (
    // Fallback Textarea when no data is available
    <Textarea
      label="Description"
      value={description}
      onChange={(e) => setDescription(e.target.value)}
      minRows={3}
      placeholder="Enter description"
      maxLength={200}
    />
  );
}

function MantinePhysicalStateSelect({ title, physicalState, setPhysicalState, productStateList }) {
  const intl = useIntl();
  return (
    <MSelect
      label={<FormattedMessage id="product.physicalstate" />} // Label for the dropdown
      value={physicalState} // Current selected value
      onChange={setPhysicalState} // Function to handle changes
      data={productStateList
        .filter((product) => product.id_category === title && product.product_state !== null)
        .sort((a, b) => {
          if (a.mps_id !== b.mps_id) {
            return a.mps_id - b.mps_id; // Sort numerically by ActionId
          }
          const stateA = a.product_state || '';
          const stateB = b.product_state || '';
          return stateA.localeCompare(stateB);
        })
        .map((product, index) => ({
          value: product.product_state,
          label: `${index}: ${product.product_state} - ${product.ActionName}`,
        }))}
      placeholder={intl.formatMessage({ id: 'product.physicalstate' })} 
      dropdownPosition="bottom" // Position dropdown below the input
      styles={{
        dropdown: { maxHeight: 150, overflowY: 'auto' }, // Limit dropdown height
        label: { marginBottom: 8 }, // Optional: Adjust label styles
        item: { whiteSpace: 'nowrap' }, // Prevent text wrapping in dropdown
      }}
      withinPortal={false} // Keeps the dropdown in the DOM hierarchy
    />
  );
}

function CreatePropertiesModal({ toggle, modal, selectedData, onSave, onDraftSave }) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const userinfo = JSON.parse(localStorage.getItem('userinfo'));
  
  // Redux selectors
  const { 
    productStateList, 
    productsList, 
    UOMList, 
    locationsList 
  } = useSelector((state) => state.products);

  // State management
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [city, setCity] = useState('');
  const [physicalState, setPhysicalState] = useState('');
  const [imageFile, setImageFile] = useState('');
  const [selectedUOM, setSelectedUOM] = useState('');
  const [savingData, setSavingData] = useState(false);
  const [savingDraftData, setSavingDraftData] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Prepare product list
  const sortedProductList = Array.from(productsList)
    .filter(item => typeof item.label === 'string')
    .sort((a, b) => a.label.localeCompare(b.label));

  // Calculate smallest product IDs
  const calculateSmallestProductIds = () => {
    const smallestProductIds = {};
    sortedProductList.forEach(product => {
      if (!smallestProductIds[product.id_category] || product.id < smallestProductIds[product.id_category].id) {
        smallestProductIds[product.id_category] = { id: product.id_category, label: product.label };
      }
    });
    return smallestProductIds;
  };
  const smallestProductIds = calculateSmallestProductIds();

  // Effects
  useEffect(() => {
    if (modal) {
      dispatch(getProductNameDescription());
      dispatch(getLocationsList());
      dispatch(getProductStateList());
      dispatch(getProductsList());
      dispatch(getCategories());
      dispatch(getUOMList());
    }
  }, [modal, dispatch]);

  useEffect(() => {
    if (!modal) {
      clearData();
    }
  }, [modal]);

  useEffect(() => {
    const selectedOption = Object.values(smallestProductIds).find(option => option.label === inputValue.value);
    if (selectedOption) {
      setTitle(parseInt(selectedOption.id));
    }
  }, [inputValue, smallestProductIds]);

  // Handle quantity change with string conversion
  const handleQuantityChange = (value) => {
    // Convert to string, handling null/undefined
    const stringValue = value === null || value === undefined ? '' : String(value);
    setQuantity(stringValue);
  };

  // Image upload handler
  function imageUploaded(file) {
    if (file) {
      const reader = new FileReader();
      reader.onload = function () {
        const base64String = reader.result;
        setImageFile({ name: file.name, data: base64String });
      };
      reader.readAsDataURL(file);
    }
  }

  // Save handlers
  const saveData = () => {
    setSavingData(true);
    const currentUTCTimestamp = new Date().toISOString();

    const selectedLocation = locationsList.find(location => location.location === city);
    const productName = sortedProductList.find(product => product.id_category === title)?.label;
    const productRefId = productStateList.find(state => 
      state.product_state === physicalState && 
      state.id_category === title && 
      state.product_state !== null
    )?.product_ref_id || null;
    const locationsIds = selectedLocation?.id || null;
    const UOMId = UOMList.find(state => state.UOM === selectedUOM)?.UOMId || null; 
    
    // Safely convert quantity to number, defaulting to 0 if empty
    const quantityNum = quantity === '' ? 0 : Number(quantity);

    const obj = {
      productName,
      productNotes: description,
      property_img: imageFile?.data,
      physicalState,
      period: currentUTCTimestamp,
      quantity: quantityNum,
      location: city,
      ownerAccountId: userinfo.accountId,
      OwnerId: userinfo.userId,
      ProductId: productRefId,
      Quantity: quantityNum,
      UOMId,
      QuantityUOM: `${quantityNum} ${selectedUOM}`,
      RegionId: locationsIds,
      Period: currentUTCTimestamp,
      PublishId: 0,
      ProductLotDetail: description,
    };

    onSave(obj, toggle);
    setSavingData(false);
  };

  const saveDraftData = () => {
    setSavingDraftData(true);
    const currentUTCTimestamp = new Date().toISOString();

    const selectedLocation = locationsList.find(location => location.location === city);
    const productName = sortedProductList.find(product => product.id_category === title)?.label;
    const productRefId = productStateList.find(state => 
      state.product_state === physicalState && 
      state.id_category === title && 
      state.product_state !== null
    )?.product_ref_id || null;
    const locationsIds = selectedLocation?.id || null;
    const UOMId = UOMList.find(state => state.UOM === selectedUOM)?.UOMId || null; 
    
    // Safely convert quantity to number, defaulting to 0 if empty
    const quantityNum = quantity === '' ? 0 : Number(quantity);

    const draftObj = {
      productName,
      productNotes: description,
      property_img: imageFile?.data,
      physicalState,
      period: currentUTCTimestamp,
      quantity: quantityNum,
      location: city,
      ownerAccountId: userinfo.accountId,
      OwnerId: userinfo.userId,
      ProductId: productRefId,
      Quantity: quantityNum,
      UOMId,
      RegionId: locationsIds,
      Period: currentUTCTimestamp,
      PublishId: 0,
      ProductLotDetail: description,
    };

    onDraftSave(draftObj, toggle);
    setSavingDraftData(false);
  };

  // Clear data method
  const clearData = () => {
    setTitle('');
    setDescription('');
    setCity('');
    setQuantity('');
    setPhysicalState('');
    setImageFile('');
    setSelectedUOM('');
    setInputValue('');
  };

  return (
    <Modal 
      opened={modal} 
      onClose={toggle} 
      title={<FormattedMessage id="modal.createprod.add" />}
      size="xl"
      centered
    >
      <Container fluid>
        <Stack 
            spacing="md" 
            align="stretch"
            justify="flex-start"
          >
          {/* Product Name */}
          <Box>
            <Autocomplete
              label={intl.formatMessage({ id: "product.name" })}
              placeholder={intl.formatMessage({ id: "product.name" })}
              data={Object.values(smallestProductIds).map(({ label }) => label)}
              value={
                Object.values(smallestProductIds).find(
                  option => option.id === title
                )?.label || ''
              }
              onChange={(value) => {
                const selectedOption = Object.values(smallestProductIds).find(
                  option => option.label === value
                );
                if (selectedOption) {
                  setTitle(selectedOption.id);
                }
              }}
              withScrollArea
              sx={{
                '.mantine-Autocomplete-dropdown': {
                  maxHeight: '200px',
                  height: 'auto',
                },
                '.mantine-Autocomplete-item': {
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Box>

          {/* Description */}
          <Box>
            <DescriptionList
              title={title}
              description={description}
              setDescription={setDescription}
            />
          </Box>

          {/* Quantity and UOM */}
          <Grid>
            <Grid.Col span={6}>
              <NumberInput
                label={intl.formatMessage({ id: "product.quantity" })}
                value={quantity}
                onChange={handleQuantityChange}
                min={0}
                placeholder={intl.formatMessage({ id: "product.quantity" })}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                formatter={(value) => 
                  !Number.isNaN(parseFloat(value)) 
                    ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') 
                    : ''
                }
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <MSelect
                label={<FormattedMessage id="product.uom" />}
                value={selectedUOM}
                onChange={setSelectedUOM}
                data={UOMList.map((uom) => ({
                  value: uom.UOM,
                  label: uom.UOM,
                }))}
                placeholder={intl.formatMessage({ id: "product.uom" })}
              />
            </Grid.Col>
          </Grid>

          {/* Physical State */}
          <Box>
            <MantinePhysicalStateSelect
              title={title}
              physicalState={physicalState}
              setPhysicalState={setPhysicalState}
              productStateList={productStateList}
            />
          </Box>

          {/* Location */}
          <Box>
            <Autocomplete
              label={intl.formatMessage({ id: "product.location" })}
              placeholder={intl.formatMessage({ id: "product.location" })}
              data={locationsList.map(location => location.location)}
              value={city}
              onChange={setCity}
              withScrollArea
              sx={{
                '.mantine-Autocomplete-dropdown': {
                  maxHeight: '200px',
                  height: 'auto',
                },
                '.mantine-Autocomplete-item': {
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Box>

          {/* Image Upload */}
          <Box>
            <Group>
              <FileButton onChange={imageUploaded} accept="image/*">
                {(props) => (
                  <MButton {...props}>
                    <FormattedMessage id="button.chooseimg" />
                  </MButton>
                )}
              </FileButton>
              {imageFile && (
                <Box c="dimmed" fz="sm">
                  {imageFile.name}
                </Box>
              )}
            </Group>
          </Box>

          {/* Action Buttons */}
          <Group position="right" mt="md">
            <MButton 
              color="gray" 
              variant="outline"
              onClick={toggle}
            >
              <FormattedMessage id="button.cancel" />
            </MButton>
            <MButton 
              color="blue" 
              variant="outline"
              onClick={saveDraftData}
              loading={savingDraftData}
            >
              <FormattedMessage id="button.savedraft" />
            </MButton>
            <MButton 
              color="green"
              onClick={saveData}
              loading={savingData}
            >
              <FormattedMessage id="button.save" />
            </MButton>
          </Group>
        </Stack>
      </Container>
    </Modal>
  );
}

export default CreatePropertiesModal;